import {
  asFiles,
  asNumber,
  asRange,
  asString,
  asStringArray,
  FIELD_TYPES,
  formatNumber,
  formulaError,
  formulaValue,
  parseDice,
  selectOption,
  voteScore,
  type CollectionData,
  type FieldDef,
  type FieldType,
  type Person,
  type RecordData,
} from '@tomo/core';
import { Avatar, AvatarStack, cx, StatBar, Tag, Tooltip, type PaletteColor } from '@tomo/ui';
import { ArrowBigDown, ArrowBigUp, CircleAlert, Globe, Star } from 'lucide-react';
import type { MouseEvent, ReactNode } from 'react';
import { useCollection, usePersonMap, useProject, useReadOnly, useRecordMap } from '../../data/hooks.tsx';
import { useIdentity } from '../../data/identity.ts';
import { iconByName, Icon } from '../../lib/icons.tsx';
import { daysFromToday, formatDate } from '../../lib/time.ts';
import { AssetThumb } from '../assets/AssetThumb.tsx';
import { RecordLink } from '../windows/RecordLink.tsx';
import './fields.css';

export function FieldTypeIcon({ type, size = 15, className }: { type: FieldType; size?: number; className?: string }) {
  const C = iconByName(FIELD_TYPES[type].icon);
  return <C size={size} className={className} aria-hidden="true" />;
}

/* ---------------------------------------------------------------------------------------------- */

export function Stars({ value, max = 5, onChange, size = 14 }: { value: number | null; max?: number; onChange?: (v: number | null) => void; size?: number }) {
  const v = value ?? 0;
  return (
    <span className="stars" data-interactive={onChange ? '' : undefined} role={onChange ? 'radiogroup' : 'img'} aria-label={`${v} of ${max}`}>
      {Array.from({ length: max }, (_, i) => {
        const filled = i < v;
        const star = <Star size={size} className="stars__star" data-filled={filled || undefined} fill={filled ? 'currentColor' : 'none'} />;
        if (!onChange) return <span key={i}>{star}</span>;
        return (
          <button
            key={i}
            type="button"
            role="radio"
            aria-checked={i + 1 === v}
            aria-label={`${i + 1}`}
            onClick={(e) => {
              e.stopPropagation();
              onChange(i + 1 === v ? null : i + 1);
            }}
          >
            {star}
          </button>
        );
      })}
    </span>
  );
}

export function VoteButton({ record, field, size = 'md' }: { record: RecordData; field?: FieldDef; size?: 'sm' | 'md' }) {
  const project = useProject();
  const readOnly = useReadOnly();
  const me = useIdentity();
  const mine = record.votes[me.id] ?? 0;
  const score = voteScore(record);
  const down = !!field?.config.allowDownvotes;
  const vote = (e: MouseEvent, v: 1 | -1) => {
    e.stopPropagation();
    project.voteRecord(record.id, mine === v ? 0 : v);
  };
  const voters = Object.keys(record.votes).length;
  return (
    <span className="vote" data-size={size} data-voted={mine > 0 ? 'up' : mine < 0 ? 'down' : undefined}>
      <Tooltip content={readOnly ? `${voters} voted` : mine > 0 ? 'Remove your vote' : `Vote${voters ? ` · ${voters} voted` : ''}`}>
        <button type="button" className="vote__btn" aria-pressed={mine > 0} aria-label="Upvote" disabled={readOnly} onClick={(e) => vote(e, 1)}>
          <ArrowBigUp size={size === 'sm' ? 14 : 16} fill={mine > 0 ? 'currentColor' : 'none'} />
        </button>
      </Tooltip>
      <span className="vote__score tabular">{score}</span>
      {down && (
        <button type="button" className="vote__btn vote__btn--down" aria-pressed={mine < 0} aria-label="Downvote" disabled={readOnly} onClick={(e) => vote(e, -1)}>
          <ArrowBigDown size={size === 'sm' ? 14 : 16} fill={mine < 0 ? 'currentColor' : 'none'} />
        </button>
      )}
    </span>
  );
}

export function TagList({ field, ids, max, size = 'md' }: { field: FieldDef; ids: string[]; max?: number; size?: 'sm' | 'md' }) {
  const opts = ids.map((id) => selectOption(field, id)).filter(Boolean) as NonNullable<ReturnType<typeof selectOption>>[];
  const shown = max ? opts.slice(0, max) : opts;
  return (
    <span className="tag-list">
      {shown.map((o) => (
        <Tag key={o.id} color={o.color} size={size}>
          {o.label}
        </Tag>
      ))}
      {max && opts.length > max && (
        <Tag size={size} color="gray">
          +{opts.length - max}
        </Tag>
      )}
    </span>
  );
}

export function PersonList({ ids, people, variant = 'full' }: { ids: string[]; people: Map<string, Person>; variant?: 'full' | 'avatars' }) {
  const list = ids.map((id) => people.get(id) ?? { id, name: 'Unknown', color: '#8a8f9c' });
  if (!list.length) return null;
  if (variant === 'avatars' || list.length > 2) return <AvatarStack people={list} size={20} max={4} />;
  return (
    <span className="person-list">
      {list.map((p) => (
        <span key={p.id} className="person-chip">
          <Avatar name={p.name} color={p.color} size={18} />
          <span className="g-truncate">{p.name}</span>
        </span>
      ))}
    </span>
  );
}

export function RecordChip({ record, link = true }: { record: RecordData; link?: boolean }) {
  const project = useProject();
  const collection = useCollection(record.collectionId);
  // Any record can carry a picture, so a chip shows it whatever collection the record is in.
  const file = record.cover;
  const inner = (
    <>
      {file ? <AssetThumb file={file} className="record-chip__thumb" /> : <Icon token={record.icon ?? collection?.icon} size={13} className="record-chip__icon" />}
      <span className="g-truncate">{record.title || 'Untitled'}</span>
    </>
  );
  if (!link) return <span className="record-chip">{inner}</span>;
  return (
    <RecordLink id={record.id} className="record-chip" onClick={(e) => e.stopPropagation()}>
      {inner}
    </RecordLink>
  );
}

export function RelationList({ ids, max, thumbs }: { ids: string[]; max?: number; thumbs?: boolean }) {
  const records = useRecordMap();
  const project = useProject();
  const list = ids.map((id) => records.get(id)).filter(Boolean) as RecordData[];
  // Thumbnails only when the linked records actually have covers, so a plain relation stays text.
  if (thumbs && list.some((r) => r.cover)) {
    return (
      <span className="relation-thumbs">
        {list.slice(0, max ?? 4).map((r) => (
          <Tooltip key={r.id} content={r.title}>
            <span>
              <AssetThumb file={r.cover} className="relation-thumbs__item" />
            </span>
          </Tooltip>
        ))}
        {max && list.length > max && <span className="relation-thumbs__more">+{list.length - max}</span>}
      </span>
    );
  }
  const shown = max ? list.slice(0, max) : list;
  return (
    <span className="relation-list">
      {shown.map((r) => (
        <RecordChip key={r.id} record={r} />
      ))}
      {max && list.length > max && <span className="relation-list__more">+{list.length - max}</span>}
    </span>
  );
}

/* ---------------------------------------------------------------------------------------------- */

export type ValueVariant = 'cell' | 'card' | 'panel';

export interface FieldValueProps {
  record: RecordData;
  field: FieldDef;
  collection: CollectionData;
  variant?: ValueVariant;
  /** Show a subtle placeholder when empty (panels). */
  placeholder?: ReactNode;
}

/** Read-only rendering of one value, shared by tables, cards and property panels. */
export function FieldValue({ record, field, collection, variant = 'cell', placeholder }: FieldValueProps) {
  const people = usePersonMap();
  const project = useProject();
  const v = record.values[field.id];
  const empty = placeholder ? <span className="value-empty">{placeholder}</span> : null;
  const cfg = field.config;

  switch (field.type) {
    case 'text': {
      const s = asString(v);
      return s ? <span className={cx('value-text', variant !== 'panel' && 'g-truncate')}>{s}</span> : empty;
    }
    case 'url': {
      const s = asString(v);
      if (!s) return empty;
      let host = s;
      try {
        host = new URL(s.includes('://') ? s : `https://${s}`).hostname.replace(/^www\./, '');
      } catch {
        /* keep raw */
      }
      return (
        <a className="value-url" href={s.includes('://') ? s : `https://${s}`} target="_blank" rel="noreferrer noopener" onClick={(e) => e.stopPropagation()}>
          <Globe size={13} />
          <span className="g-truncate">{host}</span>
        </a>
      );
    }
    case 'number': {
      const n = asNumber(v);
      if (n === null) return empty;
      const text = formatNumber(n, cfg);
      if (cfg.display === 'bar' && cfg.max) {
        return (
          <span className="value-bar">
            <span className="value-number tabular">{text}</span>
            <StatBar value={n} min={cfg.min ?? 0} max={cfg.max} color={collection.color as PaletteColor} size={variant === 'panel' ? 'sm' : 'xs'} />
          </span>
        );
      }
      return <span className="value-number tabular">{text}</span>;
    }
    case 'formula': {
      const err = formulaError(field, collection.fields);
      if (err) {
        return (
          <Tooltip content={err}>
            <span className="value-error">
              <CircleAlert size={13} /> Error
            </span>
          </Tooltip>
        );
      }
      const n = formulaValue(record, field, { ...project.valueContext(), fields: collection.fields });
      return n === null ? empty : <span className="value-number value-formula tabular">{formatNumber(n, cfg)}</span>;
    }
    case 'range': {
      const [a, b] = asRange(v);
      if (a === null && b === null) return empty;
      return (
        <span className="value-number tabular">
          {a === b || b === null ? formatNumber(a, cfg) : a === null ? formatNumber(b, cfg) : `${formatNumber(a, { ...cfg, suffix: '' })}–${formatNumber(b, cfg)}`}
        </span>
      );
    }
    case 'dice': {
      const s = asString(v);
      if (!s) return empty;
      const d = parseDice(s);
      return (
        <span className="value-dice">
          <span className="value-dice__expr">{s}</span>
          {d && variant !== 'card' && <span className="value-dice__avg tabular">≈{Number.isInteger(d.avg) ? d.avg : d.avg.toFixed(1)}</span>}
        </span>
      );
    }
    case 'rating':
      return asNumber(v) || variant !== 'cell' ? <Stars value={asNumber(v)} max={cfg.max ?? 5} size={variant === 'card' ? 12 : 14} /> : empty;
    case 'checkbox':
      return v === true ? <span className="value-check" aria-label="Yes">✓</span> : empty;
    case 'select': {
      const o = selectOption(field, v);
      return o ? (
        <Tag color={o.color} size={variant === 'card' ? 'sm' : 'md'} dot={field.role === 'status'}>
          {o.label}
        </Tag>
      ) : (
        empty
      );
    }
    case 'multiSelect': {
      const ids = asStringArray(v);
      return ids.length ? <TagList field={field} ids={ids} size={variant === 'card' ? 'sm' : 'md'} max={variant === 'cell' ? 3 : undefined} /> : empty;
    }
    case 'date': {
      const s = asString(v);
      if (!s) return empty;
      const days = field.role === 'due' ? daysFromToday(s) : null;
      const tone = days === null ? undefined : days < 0 ? 'overdue' : days === 0 ? 'today' : days <= 2 ? 'soon' : undefined;
      return (
        <span className="value-date" data-tone={tone}>
          {tone === 'overdue' ? `${formatDate(s)} · overdue` : tone === 'today' ? 'Today' : formatDate(s)}
        </span>
      );
    }
    case 'person': {
      const ids = asStringArray(v);
      return ids.length ? <PersonList ids={ids} people={people} variant={variant === 'card' ? 'avatars' : 'full'} /> : empty;
    }
    case 'relation': {
      const ids = asStringArray(v);
      if (!ids.length) return empty;
      const target = cfg.targetCollectionId ? project.getCollection(cfg.targetCollectionId) : undefined;
      const thumbs = variant !== 'panel';
      return <RelationList ids={ids} max={variant === 'cell' ? 3 : variant === 'card' ? 2 : undefined} thumbs={thumbs} />;
    }
    case 'color': {
      const s = asString(v);
      return s ? (
        <span className="value-color">
          <span className="value-color__swatch" style={{ background: s }} />
          <span className="g-mono">{s}</span>
        </span>
      ) : (
        empty
      );
    }
    case 'votes':
      return <VoteButton record={record} field={field} size={variant === 'panel' ? 'md' : 'sm'} />;
    case 'file': {
      const files = asFiles(v);
      if (!files.length) return empty;
      // In a cell or on a card there is only room for a few thumbnails; the panel lists them all.
      const shown = variant === 'panel' ? files : files.slice(0, 3);
      return (
        <span className="value-files">
          {shown.map((f) => (
            <Tooltip key={`${f.hash}:${f.name}`} content={f.name}>
              <span className="value-file">
                <AssetThumb file={f} className="value-file__thumb" />
                {variant === 'panel' && <span className="g-truncate">{f.name}</span>}
              </span>
            </Tooltip>
          ))}
          {files.length > shown.length && <span className="relation-thumbs__more">+{files.length - shown.length}</span>}
        </span>
      );
    }
  }
}
